import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BarChart3, Activity, ChevronRight, Ticket, Star } from "lucide-react";
import { useAthleteStatsStore } from "@/store/api/athleteStore";
import { useClientSubscriptionStore } from "@/store/api/clients/clientSubscriptionStore";

/**
 * Carte « Vue d'ensemble » compacte : un seul bloc, trois indicateurs en
 * pastilles circulaires, avec un accès direct à la page statistiques.
 */
export const StatsOverviewCard = () => {
  const { t } = useTranslation(["athlete", "common"]);
  const navigate = useNavigate();
  const { totalClasses } = useAthleteStatsStore();
  const { allSubscriptions } = useClientSubscriptionStore();

  // Un adhérent peut détenir plusieurs abonnements actifs, de plannings
  // différents : la vignette « séances restantes » du tableau de bord est un
  // TOTAL, elle doit donc les cumuler. N'en lire qu'un revenait à effacer les
  // séances du second. (Le quota qui autorise ou refuse une réservation, lui,
  // reste évalué planning par planning dans le dialogue de réservation.)
  const activeSubscriptions = useMemo(
    () => (allSubscriptions ?? []).filter((s) => s.status === "Active"),
    [allSubscriptions]
  );

  const sessionsRemaining = activeSubscriptions.reduce((acc, s) => acc + (s.sessions_remaining ?? 0), 0);
  const sessionsTotal = activeSubscriptions.length > 0
    ? activeSubscriptions.reduce((acc, s) => acc + (s.sessions_total ?? 0), 0)
    : null;
  const jokersRemaining = activeSubscriptions.reduce((acc, s) => acc + (s.jokers_remaining ?? 0), 0);

  const stats = [
    {
      title: t('stats.totalClasses'),
      value: totalClasses,
      icon: Activity,
      badge: null,
    },
    {
      title: t('stats.sessionsRemaining'),
      value: sessionsTotal !== null ? `${sessionsRemaining} / ${sessionsTotal}` : sessionsRemaining,
      icon: Ticket,
      badge: sessionsRemaining <= 5 && sessionsRemaining > 0 ? "⚠️" : sessionsRemaining === 0 ? "❌" : null,
    },
    {
      title: t('stats.jokersRemaining'),
      value: jokersRemaining,
      icon: Star,
      badge: jokersRemaining === 0 ? "😔" : null,
    },
  ];

  return (
    <section className="glass-stat-card athlete-overview-card p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold flex items-center gap-2 min-w-0">
          <BarChart3 className="h-4 w-4 text-primary shrink-0" />
          <span className="truncate">{t('dashboard.overview')}</span>
        </h2>
        <button
          onClick={() => navigate('/athlete/stats')}
          className="flex items-center gap-0.5 text-xs font-semibold text-primary shrink-0 active:scale-[0.97] transition-transform"
        >
          {t('dashboard.viewDetail', { defaultValue: 'Voir le détail' })}
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="flex min-w-0 flex-col items-center gap-1.5 text-center"
            style={{ animationDelay: `${index * 80}ms` }}
          >
            <div className="athlete-overview-icon flex h-10 w-10 items-center justify-center rounded-full border-2 border-primary/50 bg-primary/10">
              <stat.icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
            </div>
            <span className="text-lg font-bold leading-none">
              {stat.value}
              {stat.badge && <span className="ml-1 text-[10px]">{stat.badge}</span>}
            </span>
            <span className="text-[10px] text-muted-foreground leading-tight line-clamp-2">{stat.title}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
